import { TEST_HUB_URL } from '@/constants/constants';
import { feesReportQuery } from '@/store/slices/pricingSlice';
import { apiFetch } from '@/utils/apiWrapper';

const pricingUrl = `${TEST_HUB_URL}payment/pricing/`;
const feesReportUrl = `${TEST_HUB_URL}payment/fees-report/`;
const feesBillingUrl = `${TEST_HUB_URL}payment/report/`;

export enum PricingForms {
  ORGANIZATION = 'organization',
  FINAL = 'final',
}

export async function getPricing(params: { organizationId: string; companyId?: string }) {
  const { organizationId, companyId } = params;
  const query = new URLSearchParams();
  if (companyId) query.append('companyId', companyId);
  const qs = query.toString();
  const url = `${pricingUrl}${organizationId}${qs ? `?${qs}` : ''}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch pricing');
  return response.json();
}

export enum PricingSupportingCountries {
  NORTH_AMERICA = 'northAmerica',
  EUROPE = 'europe',
  OTHERS = 'others',
}

export enum PricingRateFields {
  VARIABLE = 'variable',
  FIXED = 'fixed',
  AMEX = 'amex',
  INSTANT_PAYOUT_FEE = 'instantPayoutFee',
}

export type PricingRate = {
  [PricingRateFields.VARIABLE]: string;
  [PricingRateFields.FIXED]: string;
  [PricingRateFields.AMEX]: string;
  [PricingRateFields.INSTANT_PAYOUT_FEE]: string;
};

export type DefaultRootPricing = Record<PricingSupportingCountries, PricingRate>;

export type MinimalPricing = DefaultRootPricing;

export const getDefaultRootPricing = async (params: {
  organizationId: string;
  companyId?: string;
}): Promise<DefaultRootPricing> => {
  const { organizationId } = params;
  const url = `${pricingUrl}default-root-pricing/${organizationId}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch default root pricing');
  return response.json();
};

export const getMinimalPricing = async (params: {
  organizationId: string;
  companyId?: string;
}): Promise<MinimalPricing> => {
  const { organizationId } = params;
  const url = `${pricingUrl}minimum-pricing/${organizationId}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch minimal pricing');
  return response.json();
};

export const updateCompanyPricing = async (params: {
  organizationId: string;
  companyId: string;
  pricing: MinimalPricing;
}): Promise<any> => {
  const { organizationId, companyId, pricing } = params;
  const url = `${pricingUrl}company-pricing-configuration/${organizationId}/${companyId}`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(pricing),
  });
  if (response?.ok) {
    return response;
  }
  if (!response) throw new Error('Failed to update company pricing');
  return response?.json();
};

export const getOrganizationPricing = async (organizationId: string): Promise<MinimalPricing> => {
  const url = `${pricingUrl}organization-pricing-configuration/${organizationId}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch organization pricing');
  return response.json();
};

export const updateOrganizationPricing = async (params: {
  organizationId: string;
  pricing: MinimalPricing;
}): Promise<any> => {
  const { organizationId, pricing } = params;
  const url = `${pricingUrl}organization-pricing-configuration/${organizationId}`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(pricing),
  });
  if (response?.ok) {
    return response;
  }
  if (!response) throw new Error('Failed to update organization pricing');
  return await response.json();
};

export const getPlanPricing = async (params: {
  organizationId: string;
  planId: string;
}): Promise<MinimalPricing> => {
  const { organizationId, planId } = params;
  const url = `${pricingUrl}plan-pricing-configuration/${organizationId}/${planId}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch plan pricing');
  return response.json();
};

export const updatePlanPricing = async (params: {
  organizationId: string;
  planId: string;
  pricing: MinimalPricing;
}): Promise<any> => {
  const { organizationId, planId, pricing } = params;
  const url = `${pricingUrl}plan-pricing-configuration/${organizationId}/${planId}`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(pricing),
  });
  if (response?.ok) {
    return response.json();
  }
  if (!response) throw new Error('Failed to update plan pricing');
  return response?.json();
};

export const getFixedFees = async (companyId: string) => {
  const url = `${pricingUrl}get-fixed-fees/${companyId}`;

  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch fixed fees');
  return response.json();
};

export const getCompanyPricing = async (companyId: string) => {
  const url = `${pricingUrl}get-company-plan-pricing/${companyId}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch fixed fees');
  return response.json();
};

export const getCurrentFeesReport = async (companyId: string) => {
  const url = `${feesReportUrl}get-current-fees-report/${companyId}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch');
  return response.json();
};

export const getFeesReportHistory = async (
  companyId: string,
  query?: {
    limit?: number;
    offset?: number;
    sortColumn?: string;
    sortValue?: 'asc' | 'desc';
  },
) => {
  const queryParams = new URLSearchParams();
  const limit = query?.limit !== undefined && query.limit !== null ? query.limit : 25;
  const offset = query?.offset !== undefined && query.offset !== null ? query.offset : 0;

  queryParams.append('limit', limit.toString());
  queryParams.append('offset', offset.toString());

  if (query?.sortColumn) {
    queryParams.append('sortColumn', query.sortColumn);
  }
  if (query?.sortValue) {
    queryParams.append('sortValue', query.sortValue);
  }

  const queryString = queryParams.toString();

  const url = `${feesReportUrl}get-fees-report-history/${companyId}?${queryString}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch');
  return response.json();
};

export const getFeesBillingByReportId = async (
  feesReportId: string,
  query?: { searchValue?: string; limit?: number; offset?: number },
) => {
  const queryParams = new URLSearchParams();
  if (query?.searchValue) queryParams.append('searchValue', query.searchValue);
  if (query?.limit !== undefined) {
    queryParams.append('limit', query.limit.toString());
  }
  if (query?.offset !== undefined) {
    queryParams.append('offset', query.offset.toString());
  }
  const queryString = queryParams.toString();
  const url = `${feesBillingUrl}get-platform-billing/${feesReportId}${queryString ? `?${queryString}` : ''}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch');
  return response.json();
};

export const getFeesBillingByOrganization = async (organizationId: string) => {
  const url = `${feesReportUrl}get-platform-billing-by-org/${organizationId}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch');
  return response.json();
};

export const getFeesReportByQuery = async (companyId: string, query: feesReportQuery) => {
  const queryParams = new URLSearchParams();
  if (query.searchValue) queryParams.append('searchValue', query.searchValue);
  if (query.status && Array.isArray(query.status)) {
    query.status.forEach((status) => {
      queryParams.append('status', status);
    });
  }
  if (query.limit !== undefined) {
    queryParams.append('limit', query.limit.toString());
  }
  if (query.offset !== undefined) {
    queryParams.append('offset', query.offset.toString());
  }
  if (query.sortBy) {
    queryParams.append('sortColumn', query.sortBy);
  }
  if (query.sortOrder) {
    queryParams.append('sortValue', query.sortOrder);
  }

  const queryString = queryParams.toString();

  const url = `${feesReportUrl}get-fees-report-by-query/${companyId}?${queryString}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch');
  return response.json();
};

export const markFeesReportAsPaid = async (
  companyId: string,
  feesReportId: string,
  status: string = 'paid',
) => {
  const url = `${feesReportUrl}marked-as-paid/${companyId}/${feesReportId}`;

  const response = await apiFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, feesReportId, status }),
  });
  if (!response) throw new Error('Failed to update');
  return response.json();
};
